import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading, EmptyState, StatCard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { DollarSign, ShoppingCart, CheckCircle2, Tag, Plus, Trash2, Send, Upload, Search, Mail, History, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/helpers";

const payStyles = {
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 border-amber-200",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400 border-red-200",
  expired: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300 border-slate-200",
};

export default function Sales() {
  const [txs, setTxs] = useState(null);
  const [linkTx, setLinkTx] = useState(null);
  const [resendTx, setResendTx] = useState(null);
  const [historyTx, setHistoryTx] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => { const { data } = await api.get("/payments/transactions"); setTxs(data); };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const paid = (txs || []).filter((t) => t.payment_status === "paid");
    return {
      revenue: paid.reduce((s, t) => s + (t.amount || 0), 0),
      orders: paid.length,
      total: (txs || []).length,
    };
  }, [txs]);

  if (!txs) return <Loading />;

  return (
    <div>
      <PageHeader title="Course Sales" subtitle="Candidate purchases from the public course shop." testid="sales-header">
        <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-bookings-button"><Upload className="h-4 w-4 mr-1.5" />Import bookings</Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Revenue" value={`£${stats.revenue.toLocaleString()}`} icon={DollarSign} accent="text-emerald-600" testid="sales-revenue" />
        <StatCard label="Paid Orders" value={stats.orders} icon={CheckCircle2} testid="sales-orders" />
        <StatCard label="Total Checkouts" value={stats.total} icon={ShoppingCart} testid="sales-total" />
      </div>

      {txs.length === 0 ? (
        <EmptyState message="No course purchases yet. Share your public shop link to start selling." icon={ShoppingCart} />
      ) : (
        <Card>
          <div className="p-4 border-b border-border">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by candidate, email or course..." className="pl-9" data-testid="search-sales-input" />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead><TableHead>Course</TableHead><TableHead>Amount</TableHead>
                <TableHead>Status</TableHead><TableHead>Access</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txs.filter((t) => { const s = q.trim().toLowerCase(); return !s || (t.candidate_name||"").toLowerCase().includes(s) || (t.candidate_email||"").toLowerCase().includes(s) || (t.course_title||"").toLowerCase().includes(s); }).map((t) => (
                <TableRow key={t.session_id} data-testid={`sale-row-${t.session_id}`}>
                  <TableCell>
                    <p className="font-medium">{t.candidate_name}</p>
                    <p className="text-xs text-muted-foreground">{t.candidate_email}</p>
                  </TableCell>
                  <TableCell>{t.course_title}</TableCell>
                  <TableCell className="font-mono">£{t.amount}{t.promo_code && <span className="ml-1 text-xs text-emerald-600">({t.promo_code})</span>}</TableCell>
                  <TableCell><Badge variant="outline" className={`capitalize border ${payStyles[t.payment_status] || payStyles.pending}`}>{t.payment_status}</Badge></TableCell>
                  <TableCell>{t.access_granted ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Emailed</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditTx(t)} data-testid={`edit-candidate-${t.session_id}-button`}>
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResendTx(t)} data-testid={`resend-email-${t.session_id}-button`}>
                      <Mail className="h-3.5 w-3.5 mr-1.5" />Resend email
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setHistoryTx(t)} data-testid={`email-history-${t.session_id}-button`}>
                      <History className="h-3.5 w-3.5 mr-1.5" />History
                    </Button>
                    {t.course_type === "cpd" && t.payment_status === "paid" && (
                      <div className="flex flex-col items-end gap-0.5">
                        <Button size="sm" variant="outline" onClick={() => setLinkTx(t)} data-testid={`send-cpd-link-${t.session_id}-button`}>
                          <Send className="h-3.5 w-3.5 mr-1.5" />{t.course_link_sent ? "Resend link" : "Send link"}
                        </Button>
                        {t.course_link_sent && <span className="text-[11px] text-emerald-600">Link sent</span>}
                      </div>
                    )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <PromoManager />

      {linkTx && <SendLinkDialog tx={linkTx} onClose={() => setLinkTx(null)} onSent={load} />}
      {resendTx && <ResendDialog tx={resendTx} onClose={() => setResendTx(null)} />}
      {historyTx && <EmailHistoryDialog tx={historyTx} onClose={() => setHistoryTx(null)} />}
      {editTx && <EditCandidateDialog tx={editTx} onClose={() => setEditTx(null)} onSaved={load} />}
      {importOpen && <BookingsImportDialog open={importOpen} setOpen={setImportOpen} onSaved={load} />}
    </div>
  );
}

function BookingsImportDialog({ open, setOpen, onSaved }) {
  const [text, setText] = useState("");
  const [sendLinks, setSendLinks] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setText(String(r.result || "")); r.readAsText(f);
  };
  const template = "name,email,course,date,amount\nJane Doe,jane@example.com,Safeguarding Level 1,2026-07-15,0\n";
  const downloadTemplate = () => {
    const blob = new Blob([template], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "bookings_template.csv"; a.click();
  };
  const upload = async () => {
    if (!text.trim()) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/bookings/bulk", { csv: text, send_links: sendLinks });
      setResult(data);
      if (data.created) { toast.success(`${data.created} booking(s) imported`); onSaved(); }
      else toast.error("No bookings imported — check the errors");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Import bookings</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Transfer existing bookings from your old system. CSV columns: <span className="font-mono text-xs">name, email, course, date, amount</span>. Course is the title or ID; date (optional) matches a live session on that day.</p>
          <button onClick={downloadTemplate} className="text-sm text-primary hover:underline" data-testid="bookings-template-download">Download CSV template</button>
          <Input type="file" accept=".csv" onChange={onFile} data-testid="bookings-csv-input" />
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Email access links to attendees</p>
              <p className="text-xs text-muted-foreground">Leave off when migrating old bookings to avoid spamming them.</p>
            </div>
            <Switch checked={sendLinks} onCheckedChange={setSendLinks} data-testid="bookings-send-links-switch" />
          </div>
          {result && (
            <div className="text-sm rounded-lg border border-border p-3 max-h-48 overflow-auto" data-testid="bookings-import-result">
              <p className="text-emerald-600 font-medium">{result.created} imported</p>
              {result.errors?.length > 0 && <ul className="mt-1 list-disc pl-4 text-red-600">{result.errors.map((er, i) => <li key={i}>Row {er.row}: {er.message}</li>)}</ul>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button onClick={upload} disabled={busy} data-testid="bookings-upload-button">{busy ? "Importing…" : "Import"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResendDialog({ tx, onClose }) {
  const options = [
    ["confirmation", "Payment confirmation + VAT receipt", tx.payment_status === "paid" && tx.billing !== "order" && tx.billing !== "manual" && tx.billing !== "basket"],
    ["access", "Course access link", true],
    ["certificate", "Certificate", !!tx.certificate && tx.attendance === "present"],
    ["handouts", "Course handouts", true],
    ["cpd_link", "CPD course link", !!tx.course_link_sent],
    ["invoice", "Order invoice (to the order contact)", !!tx.order_id],
  ].filter(([, , show]) => show);
  const [kind, setKind] = useState(options[0][0]);
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/payments/transactions/${tx.session_id}/resend`, { kind });
      toast.success(`Email sent to ${data.to}`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="resend-email-dialog">
        <DialogHeader><DialogTitle className="font-heading">Resend an email</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{tx.candidate_name} · <span className="font-mono">{tx.candidate_email}</span> · {tx.course_title}</p>
        <div className="space-y-2">
          {options.map(([value, label]) => (
            <label key={value} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer text-sm ${kind === value ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`resend-option-${value}`}>
              <input type="radio" name="resend-kind" checked={kind === value} onChange={() => setKind(value)} />{label}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="resend-email-send-button"><Send className="h-4 w-4 mr-1.5" />{sending ? "Sending..." : "Send email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCandidateDialog({ tx, onClose, onSaved }) {
  const [name, setName] = useState(tx.candidate_name || "");
  const [email, setEmail] = useState(tx.candidate_email || "");
  const [resend, setResend] = useState(true);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/payments/transactions/${tx.session_id}/candidate`, { candidate_name: name, candidate_email: email, resend_access: resend });
      if (data.sent === false) toast.warning("Details saved, but the access email could not be sent");
      else toast.success(data.sent ? `Details saved and access link sent to ${data.candidate_email}` : "Details saved");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-candidate-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit candidate details</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{tx.course_title}</p>
        <div className="space-y-3">
          <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="edit-candidate-name-input" /></div>
          <div><Label>Email address</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="edit-candidate-email-input" /></div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Send access link to this email</p>
              <p className="text-xs text-muted-foreground">Useful when the original email was mistyped.</p>
            </div>
            <Switch checked={resend} onCheckedChange={setResend} data-testid="edit-candidate-resend-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !name.trim() || !email.trim()} data-testid="edit-candidate-save-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailHistoryDialog({ tx, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/payments/transactions/${tx.session_id}/emails`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [tx.session_id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="email-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Email history</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">All emails sent to <span className="font-mono">{tx.candidate_email}</span></p>
        {!rows ? <Loading /> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="email-history-empty">No emails recorded yet.</p>
        ) : (
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`email-history-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(r.sent_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/London" })}</TableCell>
                    <TableCell className="text-sm">{r.kind}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.subject}</TableCell>
                    <TableCell><Badge variant="outline" className={`capitalize border ${r.status === "sent" ? payStyles.paid : payStyles.failed}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="email-history-close-button">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendLinkDialog({ tx, onClose, onSent }) {
  const [link, setLink] = useState(tx.course_link_sent || "");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!link.trim()) return toast.error("Enter the course link");
    setSending(true);
    try {
      await api.post(`/payments/transactions/${tx.session_id}/send-link`, { link: link.trim() });
      toast.success(`Course link emailed to ${tx.candidate_email}`);
      onSent();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Send course link</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Email a personal access link for <span className="font-medium text-foreground">{tx.course_title}</span> to {tx.candidate_name} (<span className="font-mono">{tx.candidate_email}</span>).</p>
          <div><Label>Course link</Label><Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." className="mt-1.5" data-testid="cpd-link-input" /></div>
          {tx.course_link_sent && <p className="text-xs text-muted-foreground break-all">Previously sent: {tx.course_link_sent}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="send-cpd-link-confirm-button"><Send className="h-4 w-4 mr-1.5" />{sending ? "Sending..." : "Send link"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromoManager() {
  const [promos, setPromos] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", amount: 10, active: true });
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/promo"); setPromos(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim()) return toast.error("Enter a code");
    setSaving(true);
    try {
      await api.post("/promo", { ...form, code: form.code.trim().toUpperCase(), amount: Number(form.amount) });
      toast.success("Promo code created");
      setForm({ code: "", discount_type: "percent", amount: 10, active: true });
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => { await api.put(`/promo/${p.id}`); load(); };
  const remove = async (p) => { await api.delete(`/promo/${p.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="mt-10">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight flex items-center gap-2"><Tag className="h-5 w-5 text-primary" />Discount Codes</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Promo codes candidates can apply at checkout.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="create-promo-button"><Plus className="h-4 w-4 mr-1.5" />New Code</Button>
      </div>

      {!promos ? <Loading /> : promos.length === 0 ? (
        <EmptyState message="No discount codes yet. Create one to offer candidates a deal." icon={Tag} />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Uses</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {promos.map((p) => (
                <TableRow key={p.id} data-testid={`promo-row-${p.id}`}>
                  <TableCell className="font-mono font-semibold">{p.code}</TableCell>
                  <TableCell>{p.discount_type === "percent" ? `${p.amount}% off` : `£${p.amount} off`}</TableCell>
                  <TableCell>{p.uses || 0}</TableCell>
                  <TableCell><Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.id}`} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-600" onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">New Discount Code</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="WELCOME10" className="mt-1.5" data-testid="promo-code-field" /></div>
            <div>
              <Label>Discount type</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm((f) => ({ ...f, discount_type: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed amount (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{form.discount_type === "percent" ? "Percent off" : "Pounds off"}</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5" data-testid="promo-amount-field" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-promo-button">{saving ? "Saving..." : "Create Code"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
